/**
 * DocumentService - Real implementation with DMS API integration
 * 
 * This service fetches real document data from the DMS API.
 */

import apiClient from '../../../../api/axios';
import { AstToTextConverter, DatabaseAST } from '../../services/AstToTextConverter';
import { DocumentLogger } from '@/utils/loggers';

export interface DocumentMeta {
  mime: string;
  capabilities: {
    requiresPagination: boolean;
  };
  title?: string;
  versionNumber?: string;
}

export class DocumentService {
  private documentCache: Map<string, any> = new Map();
  private inFlightMeta: Map<string, Promise<DocumentMeta>> = new Map();

  constructor(options: any = {}) {
    console.log('📄 [DocumentService] Real implementation created');
  }

  private isUuidLike(value: string | undefined | null): boolean {
    if (!value) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  private resolveVersionIdFromCachedMeta(cachedDoc: any, versionRef: string): string | undefined {
    if (!cachedDoc || !versionRef) return undefined;

    const normalizedRef = versionRef.trim();
    const versions = Array.isArray(cachedDoc.versions) ? cachedDoc.versions : [];
    const getVersionId = (item: any): string | undefined => item?.version_id || item?.id;

    const byId = versions.find((version: any) =>
      version?.version_id === normalizedRef || version?.id === normalizedRef
    );
    const byIdResolved = getVersionId(byId);
    if (byIdResolved) return byIdResolved;

    const byVersionNumber = versions.find((version: any) =>
      version?.version_number === normalizedRef || version?.versionNumber === normalizedRef
    );
    const byVersionNumberResolved = getVersionId(byVersionNumber);
    if (byVersionNumberResolved) return byVersionNumberResolved;

    const latest = cachedDoc.latest_version;
    if (latest) {
      if (latest.version_id === normalizedRef || latest.id === normalizedRef) {
        return getVersionId(latest);
      }
      if (latest.version_number === normalizedRef || latest.versionNumber === normalizedRef) {
        return getVersionId(latest);
      }
    }

    return undefined;
  }

  private async resolveTargetVersionId(docId: string, versionRef?: string): Promise<string | undefined> {
    const normalizedRef = String(versionRef || '').trim();
    const cached = this.documentCache.get(docId);

    if (!normalizedRef || normalizedRef === 'active') {
      const latestFromCache = cached?.latest_version?.version_id;
      if (latestFromCache) return latestFromCache;
      await this.getDocumentMeta(docId);
      const refreshed = this.documentCache.get(docId);
      return refreshed?.latest_version?.version_id;
    }

    if (this.isUuidLike(normalizedRef)) {
      return normalizedRef;
    }

    const fromCache = this.resolveVersionIdFromCachedMeta(cached, normalizedRef);
    if (fromCache) return fromCache;

    try {
      await this.getDocumentMeta(docId);
      const refreshed = this.documentCache.get(docId);
      const fromRefreshed = this.resolveVersionIdFromCachedMeta(refreshed, normalizedRef);
      if (fromRefreshed) return fromRefreshed;
    } catch (error) {
      console.warn('⚠️ [DocumentService] Failed to refresh meta while resolving version ID:', error);
    }

    return undefined;
  }

  async getDocumentMeta(docId: string): Promise<DocumentMeta> {
    // ✅ FIX: Guard against empty docId to prevent invalid API calls
    if (!docId || docId.trim() === '') {
      console.warn('⚠️ [DocumentService] getDocumentMeta called with empty docId, returning default meta');
      return { mime: 'application/edgelex', capabilities: { requiresPagination: true } };
    }
    
    // Fast path: return from cache if available
    const cached = this.documentCache.get(docId);
    if (cached) {
      console.log('📄 [DocumentService] getDocumentMeta cache HIT for docId:', docId, {
        cachedTitle: cached.title,
        hasCachedTitle: !!cached.title,
        cachedTitleType: typeof cached.title
      });
      return {
        mime: cached.latest_version?.mime_type || cached.mime_type || 'application/edgelex',
        capabilities: {
          requiresPagination: !String(cached.latest_version?.mime_type || cached.mime_type || '')
            .toLowerCase()
            .includes('application/pdf'),
        },
        title: cached.title || 'Untitled document',
        versionNumber: cached.latest_version?.version_number
      };
    }

    // Deduplicate concurrent calls
    const existing = this.inFlightMeta.get(docId);
    if (existing) {
      console.log('📄 [DocumentService] getDocumentMeta COALESCED (in-flight) for docId:', docId);
      return existing;
    }

    const p = (async (): Promise<DocumentMeta> => {
      try {
      console.log('📄 [DocumentService] getDocumentMeta (real API) for docId:', docId);
      console.log('🔐 [DocumentService] Using axios with automatic token injection');

      const apiUrl = `/dms/documents/${docId}?versions=all`;
      console.log('🌐 [DocumentService] Making API call to:', apiUrl);

      // Call the DMS API to get document with version details using axios (with automatic token injection)
      const response = await apiClient.get(apiUrl);

      console.log('📡 [DocumentService] API Response status:', response.status, response.statusText);

      const responseData = response.data;
      console.log('📦 [DocumentService] Raw API response:', responseData);
      
      // The API can return either { document } or a flat document
      const documentData = responseData.document || responseData;
      
      if (!documentData || typeof documentData !== 'object') {
        console.error('❌ [DocumentService] No document data in response. Full response:', responseData);
        return { mime: 'application/edgelex', capabilities: { requiresPagination: true } };
      }

      // Cache the document data for other methods
      this.documentCache.set(docId, documentData);

      console.log('✅ [DocumentService] Document data loaded successfully:', {
        docId,
        title: documentData.title,
        titleType: typeof documentData.title,
        titleLength: documentData.title?.length,
        hasTitle: !!documentData.title,
        versionCount: documentData.versions?.length || 0,
        latestVersion: documentData.latest_version?.version_number,
        fullDocumentData: documentData // Log full object for debugging
      });

      const resolvedMime = documentData.latest_version?.mime_type || documentData.mime_type || 'application/edgelex';
      const isPdf = String(resolvedMime).toLowerCase().includes('application/pdf');
      const meta: DocumentMeta = {
        mime: resolvedMime,
        capabilities: { requiresPagination: !isPdf },
        title: documentData.title || 'Untitled document', // Fallback if title is missing
        versionNumber: documentData.latest_version?.version_number
      };
      
      console.log('📋 [DocumentService] Returning DocumentMeta:', {
        title: meta.title,
        hasTitle: !!meta.title
      });
      
      return meta;
    } catch (error) {
      console.error('💥 [DocumentService] Error fetching document data:', error);
      return { mime: 'application/edgelex', capabilities: { requiresPagination: true } };
    } finally {
      this.inFlightMeta.delete(docId);
    }
    })();

    this.inFlightMeta.set(docId, p);
    return p;
  }

  /**
   * Track document access for audit logging (view/edit/download/share).
   * This writes to /api/v1/dms/documents/:documentId/access-track.
   */
  async trackDocumentAccess(
    docId: string,
    accessType: 'view' | 'edit' | 'download' | 'share',
    sessionId?: string,
    versionId?: string
  ): Promise<void> {
    if (!docId || docId.trim() === '') {
      console.warn('⚠️ [DocumentService] trackDocumentAccess called with empty docId');
      return;
    }

    try {
      await apiClient.post(`/dms/documents/${docId}/access-track`, {
        access_type: accessType,
        session_id: sessionId,
        version_id: versionId
      });
    } catch (error) {
      console.error('💥 [DocumentService] Failed to track document access:', error);
    }
  }

  /**
   * Resolve the human-readable version number for a given versionId.
   * Uses cached document data first, then fetches the specific version if needed.
   */
  async resolveVersionNumber(docId: string, versionId?: string): Promise<{
    versionNumber?: string;
    source: 'cache' | 'api' | 'none';
    error?: string;
  }> {
    if (!docId || docId.trim() === '') {
      return { source: 'none', error: 'Document ID is required to resolve version number.' };
    }

    const resolveFromCache = (cachedDoc: any): string | undefined => {
      if (!cachedDoc) return undefined;
      if (versionId) {
        if (
          cachedDoc.latest_version?.version_number === versionId ||
          cachedDoc.latest_version?.versionNumber === versionId
        ) {
          return cachedDoc.latest_version?.version_number || cachedDoc.latest_version?.versionNumber;
        }

        const versions = Array.isArray(cachedDoc.versions) ? cachedDoc.versions : [];
        const match = versions.find((version: any) =>
          version?.version_id === versionId ||
          version?.id === versionId ||
          version?.version_number === versionId ||
          version?.versionNumber === versionId
        );
        if (match?.version_number) return match.version_number;
        if (match?.versionNumber) return match.versionNumber;
        if (cachedDoc.latest_version?.version_id === versionId && cachedDoc.latest_version?.version_number) {
          return cachedDoc.latest_version.version_number;
        }
        return undefined;
      }

      return cachedDoc.latest_version?.version_number;
    };

    const cached = this.documentCache.get(docId);
    const cachedResolved = resolveFromCache(cached);
    if (cachedResolved) {
      return { versionNumber: cachedResolved, source: 'cache' };
    }

    try {
      await this.getDocumentMeta(docId);
    } catch (error) {
      console.warn('⚠️ [DocumentService] resolveVersionNumber failed to refresh meta:', error);
    }

    const cachedAfterMeta = this.documentCache.get(docId);
    const resolvedAfterMeta = resolveFromCache(cachedAfterMeta);
    if (resolvedAfterMeta) {
      return { versionNumber: resolvedAfterMeta, source: 'cache' };
    }

    if (versionId) {
      try {
        const resolvedVersionId = await this.resolveTargetVersionId(docId, versionId);
        if (!resolvedVersionId) {
          return { source: 'none', error: `Unable to resolve version reference '${versionId}'.` };
        }

        const response = await apiClient.get(`/dms/documents/${docId}/versions/${encodeURIComponent(resolvedVersionId)}`);
        const versionData = response.data?.version || response.data;
        const resolved =
          versionData?.version_number ||
          versionData?.versionNumber;

        if (resolved) {
          if (cachedAfterMeta) {
            const versions = Array.isArray(cachedAfterMeta.versions) ? cachedAfterMeta.versions : [];
            const exists = versions.some((version: any) =>
              version?.version_id === resolvedVersionId || version?.id === resolvedVersionId
            );
            if (!exists) {
              cachedAfterMeta.versions = [...versions, versionData];
              this.documentCache.set(docId, cachedAfterMeta);
            }
          }

          return { versionNumber: resolved, source: 'api' };
        }

        return { source: 'none', error: 'Version data returned without version number.' };
      } catch (error) {
        console.error('💥 [DocumentService] Failed to resolve version number from API:', error);
        return { source: 'none', error: 'Failed to fetch version details.' };
      }
    }

    return { source: 'none', error: 'No version number available for document.' };
  }

  async open(docId: string): Promise<void> {
    console.log('📄 [DocumentService] open called (real API)');
    // Document is already loaded via getDocumentMeta
  }

  async close(docId: string): Promise<void> {
    console.log('📄 [DocumentService] close called (real API)');
    // Clear cache when closing
    this.documentCache.delete(docId);
  }

  async getDocumentContent(docId: string): Promise<{ text: string; metadata?: any }> {
    console.log('📄 [DocumentService] getDocumentContent called for docId:', docId);

    try {
      const contentResponse = await apiClient.get(`/dms/documents/${docId}/content`);
      const content = {
        text: contentResponse.data.text || '',
        metadata: contentResponse.data.metadata
      };

      console.log('✅ [DocumentService] Document content loaded:', {
        docId,
        textLength: content.text.length,
        hasMetadata: !!content.metadata
      });

      // Cache the content with the document data
      const cached = this.documentCache.get(docId) || {};
      this.documentCache.set(docId, {
        ...cached,
        content: content.text,
        metadata: content.metadata
      });

      return content;
    } catch (error) {
      console.error('💥 [DocumentService] Error fetching document content:', error);
      // Return empty content as fallback
      return { text: '', metadata: undefined };
    }
  }

  async getDocumentContentFromAst(docId: string, versionId?: string): Promise<{ 
    text: string; 
    metadata?: any;
    paragraphs?: Array<{
      id: string;
      type: string;
      version: number;
      textStart: number;
      textEnd: number;
      styleId?: string;
    }>;
    astHash?: string;  // Server-computed AST hash for cache invalidation
  }> {
    const startTime = performance.now();
    DocumentLogger.loadStarted(docId, { versionId });
    console.log('📄 [DocumentService] getDocumentContentFromAst called for docId:', docId, 'versionId:', versionId);

    // ✅ FIX: Guard against empty docId to prevent invalid API calls
    if (!docId || docId.trim() === '') {
      console.warn('⚠️ [DocumentService] getDocumentContentFromAst called with empty docId, returning empty content');
      return { text: '', metadata: undefined, paragraphs: undefined };
    }

    // For new/empty documents, just return empty content without trying to fetch
    const cached = this.documentCache.get(docId);
    if (cached && cached.versions && cached.versions.length === 0) {
      console.log('📄 [DocumentService] Document has no versions yet, returning empty content');
      return { text: '', metadata: undefined, paragraphs: undefined };
    }

    try {
      const targetVersionId = await this.resolveTargetVersionId(docId, versionId);
      if (!targetVersionId) {
        throw new Error(`Unable to resolve a valid version ID for document ${docId}.`);
      }

      console.log('📄 [DocumentService] Fetching AST for version:', targetVersionId);
      const astResponse = await apiClient.get(`/dms/documents/${docId}/versions/${encodeURIComponent(targetVersionId)}/ast`);
      console.log('✅ [DocumentService] AST data loaded, converting to text...');
      console.log('📦 [DocumentService] Raw AST response:', {
        ast: astResponse.data.ast,
        astBlocks: astResponse.data.ast?.blocks,
        firstBlock: astResponse.data.ast?.blocks?.[0],
        firstBlockChildren: astResponse.data.ast?.blocks?.[0]?.children,
        firstBlockText: astResponse.data.ast?.blocks?.[0]?.children?.[0]?.text,
        astVersion: astResponse.data.ast_version
      });

      // Convert AST to text
      const text = AstToTextConverter.convertToText(astResponse.data.ast);
      console.log('✅ [DocumentService] AST converted to text:', {
        docId,
        versionId: targetVersionId,
        textLength: text.length,
        extractedText: text.substring(0, 100), // First 100 chars
        astVersion: astResponse.data.ast_version
      });

      // 🔧 FIX: Extract paragraph metadata from AST for proper WASM initialization
      // Without this, WASM treats the entire document as one paragraph.
      // MS Word model (2026-01-19): \r is paragraph boundary, \n is soft line break.
      const paragraphs = AstToTextConverter.extractParagraphs(astResponse.data.ast, text);
      console.log('📄 [DocumentService] Extracted paragraphs:', {
        paragraphCount: paragraphs.length,
        paragraphBoundaries: paragraphs.map(p => `[${p.textStart}, ${p.textEnd})`),
        // 🆕 2026-01-20: Log per-paragraph formatting for debugging load issues
        withLineSpacing: paragraphs.filter(p => p.lineSpacing !== undefined).map(p => ({
          id: p.id?.substring(0, 8),
          lineSpacing: p.lineSpacing
        })),
        withFormatting: paragraphs.filter(p => p.lineSpacing !== undefined || p.fontSizePx !== undefined).length,
        // 🆕 Phase F (2026-01-21): Log formattingSpans for debugging bold/italic persistence
        withFormattingSpans: paragraphs.filter(p => p.formattingSpans && p.formattingSpans.length > 0).map(p => ({
          id: p.id?.substring(0, 8),
          spanCount: p.formattingSpans?.length || 0,
          spans: p.formattingSpans
        }))
      });

      // Cache the content
      const updatedCache = this.documentCache.get(docId) || {};
      this.documentCache.set(docId, {
        ...updatedCache,
        content: text,
        ast: astResponse.data.ast,
        metadata: astResponse.data.ast?.metadata,
        paragraphs  // 🔧 Cache paragraphs for later use
      });

      const loadTimeMs = performance.now() - startTime;
      DocumentLogger.loadCompleted(docId, {
        contentLength: text.length,
        versionsCount: paragraphs?.length || 0,
        loadTimeMs,
      });

      return {
        text,
        metadata: astResponse.data.ast?.metadata,
        paragraphs,  // 🔧 Return paragraphs to caller
        astHash: astResponse.data.astHash  // 🆕 Server-computed hash for cache key
      };
    } catch (error: any) {
      DocumentLogger.loadFailed(docId, error?.message || 'Unknown error');
      console.log('⚠️ [DocumentService] AST not available (404 expected for new documents):', error?.response?.status);

      // For 404 errors (AST not found), this is expected for new documents
      if (error?.response?.status === 404) {
        console.log('📄 [DocumentService] Document has no AST data yet (normal for new documents)');
        return { text: '', metadata: undefined, paragraphs: undefined };
      }

      // For other errors, try fallback content loading
      console.log('🔄 [DocumentService] Trying fallback content loading for non-404 error...');
      try {
        const contentResponse = await apiClient.get(`/dms/documents/${docId}/content`);
        const text = contentResponse.data.text || '';
        console.log('✅ [DocumentService] Fallback content loaded:', text.length, 'characters');

        // Cache the content
        const updatedCache = this.documentCache.get(docId) || {};
        this.documentCache.set(docId, {
          ...updatedCache,
          content: text,
          metadata: contentResponse.data.metadata
        });

        return {
          text,
          metadata: contentResponse.data.metadata,
          paragraphs: undefined  // Fallback content doesn't have paragraph metadata
        };
      } catch (fallbackError: any) {
        console.log('⚠️ [DocumentService] Fallback content also not available (404 expected):', fallbackError?.response?.status);

        // For new documents, 404 on content endpoint is also expected
        if (fallbackError?.response?.status === 404) {
          console.log('📄 [DocumentService] Document has no content yet (normal for new documents)');
          return { text: '', metadata: undefined, paragraphs: undefined };
        }

        console.error('💥 [DocumentService] Unexpected error in fallback content loading:', fallbackError);
        return { text: '', metadata: undefined, paragraphs: undefined };
      }
    }
  }

  // Helper method to get cached document data
  getCachedDocument(docId: string): any {
    return this.documentCache.get(docId);
  }

  /**
   * Save document content to Postgres via AST API
   * 
   * @param docId - Document identifier
   * @param versionId - Version identifier (or 'active' for latest)
   * @param astPayload - AST save payload from WasmStateToAstAdapter.buildSavePayload()
   * @returns Promise resolving to save result
   */
  async saveDocumentAst(
    docId: string,
    versionId: string,
    astPayload: {
      ast: any;
      ast_version: string;
      cached_layout_plan?: any;
      layout_plan_hash?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    const startTime = performance.now();
    DocumentLogger.saveStarted(docId, 'manual');
    DocumentLogger.api.payload('Saving document AST', {
      docId,
      versionId,
      blockCount: astPayload.ast?.blocks?.length || 0,
      hasLayoutPlan: !!astPayload.cached_layout_plan
    });

    // Guard against empty docId
    if (!docId || docId.trim() === '') {
      console.error('❌ [DocumentService] Cannot save: empty docId');
      return { success: false, error: 'Document ID is required' };
    }

    try {
      const targetVersionId = await this.resolveTargetVersionId(docId, versionId);

      if (!targetVersionId) {
        console.error('❌ [DocumentService] Cannot save: no version ID available');
        return { success: false, error: 'No version ID available for save' };
      }

      DocumentLogger.api.post(`/dms/documents/${docId}/versions/${targetVersionId}/ast`);
      DocumentLogger.api.payload('AST Payload structure', {
        hasAst: !!astPayload.ast,
        astVersion: astPayload.ast_version,
        blockCount: astPayload.ast?.blocks?.length || 0,
        hasMetadata: !!astPayload.ast?.metadata,
        hasLayoutPlan: !!astPayload.cached_layout_plan,
        hasLayoutPlanHash: !!astPayload.layout_plan_hash,
        metadataDocId: astPayload.ast?.metadata?.docId,
        metadataVersionId: astPayload.ast?.metadata?.versionId,
        firstBlockText: astPayload.ast?.blocks?.[0]?.children?.[0]?.text?.substring(0, 50) || 'N/A'
      });

      const response = await apiClient.post(
        `/dms/documents/${docId}/versions/${encodeURIComponent(targetVersionId)}/ast`,
        astPayload
      );

      DocumentLogger.api.success('Save', {
        status: response.status,
        statusText: response.statusText,
        docId,
        versionId: targetVersionId,
        responseData: response.data
      });

      // Update cache with new AST data
      const cached = this.documentCache.get(docId) || {};
      this.documentCache.set(docId, {
        ...cached,
        ast: astPayload.ast,
        content: this.extractTextFromAst(astPayload.ast)
      });

      const saveTimeMs = performance.now() - startTime;
      const contentLength = this.extractTextFromAst(astPayload.ast).length;
      DocumentLogger.saveCompleted(docId, {
        saveType: 'manual',
        contentLength,
        saveTimeMs,
        versionId: targetVersionId,
      });

      return { success: true };
    } catch (error: any) {
      console.error('💥 [DocumentService] Save failed:', {
        error: error.message,
        status: error?.response?.status,
        data: error?.response?.data
      });

      DocumentLogger.saveFailed(docId, 'manual', error?.message || 'Unknown error');

      return {
        success: false,
        error: error?.response?.data?.message || error.message || 'Save failed'
      };
    }
  }

  /**
   * Extract plain text from AST (helper for cache update)
   *
   * 🔧 FIX (2026-01-19): Updated to MS Word paragraph model
   * - Blocks are joined with \r (carriage return = paragraph boundary)
   * - \n (line feed) is soft line break (stays in same paragraph)
   */
  private extractTextFromAst(ast: any): string {
    if (!ast?.blocks) return '';

    return ast.blocks
      .map((block: any) =>
        block.children?.map((child: any) => child.text || '').join('') || ''
      )
      .join('\r');  // ✅ MS Word model: \r is paragraph boundary
  }
}
